import { chileCommunes, chileRegions } from './chileRegions';

const r = (names = []) => names.map((name) => ({ value: name, label: name }));
const loc = (entries = []) =>
  Object.fromEntries(entries.map(([region, names]) => [region, r(names)]));

const fold = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const COUNTRY_ALIASES = {
  chile: 'Chile',
  spain: 'España',
  espana: 'España',
  peru: 'Perú',
  argentina: 'Argentina',
  bolivia: 'Bolivia',
  brasil: 'Brasil',
  brazil: 'Brasil',
  colombia: 'Colombia',
  ecuador: 'Ecuador',
  mexico: 'México',
  paraguay: 'Paraguay',
  uruguay: 'Uruguay',
  usa: 'Estados Unidos',
  us: 'Estados Unidos',
  'estados unidos': 'Estados Unidos',
  'united states': 'Estados Unidos',
};

export const canonicalCountry = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return COUNTRY_ALIASES[fold(raw)] || raw;
};

export const COUNTRY_PHONE_META = {
  Chile: { callingCode: '56', placeholder: '+56 9 3946 6150', nationalMin: 9, nationalMax: 9, groups: 'chile' },
  España: { callingCode: '34', placeholder: '+34 612 345 678', nationalMin: 9, nationalMax: 9, groups: [3, 3, 3] },
  Perú: { callingCode: '51', placeholder: '+51 9 123 456 789', nationalMin: 8, nationalMax: 9, groups: [1, 3, 3, 3] },
  Argentina: { callingCode: '54', placeholder: '+54 11 1234 5678', nationalMin: 10, nationalMax: 11, groups: [2, 4, 4] },
  Bolivia: { callingCode: '591', placeholder: '+591 2 123 4567', nationalMin: 8, nationalMax: 8, groups: [1, 3, 4] },
  Brasil: { callingCode: '55', placeholder: '+55 11 91234 5678', nationalMin: 10, nationalMax: 11, groups: [2, 5, 4] },
  Colombia: { callingCode: '57', placeholder: '+57 310 123 4567', nationalMin: 10, nationalMax: 10, groups: [3, 3, 4] },
  Ecuador: { callingCode: '593', placeholder: '+593 99 123 4567', nationalMin: 8, nationalMax: 9, groups: [2, 3, 4] },
  México: { callingCode: '52', placeholder: '+52 55 1234 5678', nationalMin: 10, nationalMax: 10, groups: [2, 4, 4] },
  Paraguay: { callingCode: '595', placeholder: '+595 21 123 456', nationalMin: 8, nationalMax: 9, groups: [2, 3, 3] },
  Uruguay: { callingCode: '598', placeholder: '+598 94 123 456', nationalMin: 8, nationalMax: 8, groups: [2, 3, 3] },
  'Estados Unidos': { callingCode: '1', placeholder: '+1 415 555 2671', nationalMin: 10, nationalMax: 10, groups: [3, 3, 4] },
  Otro: { callingCode: '', placeholder: '+34 612 345 678', nationalMin: 8, nationalMax: 15, groups: 'intl' },
};

export const phoneMetaForCountry = (country) =>
  COUNTRY_PHONE_META[canonicalCountry(country)] || COUNTRY_PHONE_META.Otro;

const SPAIN = {
  regionLabel: 'Comunidad autónoma',
  localityLabel: 'Provincia',
  regions: r([
    'Andalucía',
    'Aragón',
    'Asturias',
    'Islas Baleares',
    'Canarias',
    'Cantabria',
    'Castilla-La Mancha',
    'Castilla y León',
    'Cataluña',
    'Comunidad Valenciana',
    'Extremadura',
    'Galicia',
    'Madrid',
    'Murcia',
    'Navarra',
    'País Vasco',
    'La Rioja',
    'Ceuta',
    'Melilla',
  ]),
  localities: loc([
    ['Andalucía', ['Almería', 'Cádiz', 'Córdoba', 'Granada', 'Huelva', 'Jaén', 'Málaga', 'Sevilla']],
    ['Aragón', ['Huesca', 'Teruel', 'Zaragoza']],
    ['Asturias', ['Asturias']],
    ['Islas Baleares', ['Islas Baleares']],
    ['Canarias', ['Las Palmas', 'Santa Cruz de Tenerife']],
    ['Cantabria', ['Cantabria']],
    ['Castilla-La Mancha', ['Albacete', 'Ciudad Real', 'Cuenca', 'Guadalajara', 'Toledo']],
    ['Castilla y León', ['Ávila', 'Burgos', 'León', 'Palencia', 'Salamanca', 'Segovia', 'Soria', 'Valladolid', 'Zamora']],
    ['Cataluña', ['Barcelona', 'Girona', 'Lleida', 'Tarragona']],
    ['Comunidad Valenciana', ['Alicante', 'Castellón', 'Valencia']],
    ['Extremadura', ['Badajoz', 'Cáceres']],
    ['Galicia', ['A Coruña', 'Lugo', 'Ourense', 'Pontevedra']],
    ['Madrid', ['Madrid']],
    ['Murcia', ['Murcia']],
    ['Navarra', ['Navarra']],
    ['País Vasco', ['Álava', 'Bizkaia', 'Gipuzkoa']],
    ['La Rioja', ['La Rioja']],
    ['Ceuta', ['Ceuta']],
    ['Melilla', ['Melilla']],
  ]),
};

const GEOGRAPHY = {
  Chile: {
    regionLabel: 'Región',
    localityLabel: 'Comuna',
    regions: chileRegions.map((region) => ({ value: region.value, label: region.label })),
    localities: chileCommunes,
  },
  España: SPAIN,
  Perú: {
    regionLabel: 'Departamento',
    localityLabel: 'Provincia',
    regions: r([
      'Amazonas', 'Áncash', 'Apurímac', 'Arequipa', 'Ayacucho', 'Cajamarca', 'Callao', 'Cusco',
      'Huancavelica', 'Huánuco', 'Ica', 'Junín', 'La Libertad', 'Lambayeque', 'Lima', 'Loreto',
      'Madre de Dios', 'Moquegua', 'Pasco', 'Piura', 'Puno', 'San Martín', 'Tacna', 'Tumbes', 'Ucayali',
    ]),
    localities: {},
  },
  Argentina: {
    regionLabel: 'Provincia',
    localityLabel: 'Localidad',
    regions: r([
      'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes', 'Entre Ríos',
      'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones', 'Neuquén', 'Río Negro',
      'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe', 'Santiago del Estero',
      'Tierra del Fuego', 'Tucumán',
    ]),
    localities: {},
  },
  Bolivia: {
    regionLabel: 'Departamento',
    localityLabel: 'Provincia',
    regions: r(['Beni', 'Chuquisaca', 'Cochabamba', 'La Paz', 'Oruro', 'Pando', 'Potosí', 'Santa Cruz', 'Tarija']),
    localities: {},
  },
  Brasil: {
    regionLabel: 'Estado',
    localityLabel: 'Ciudad',
    regions: r([
      'Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahía', 'Ceará', 'Distrito Federal', 'Espírito Santo',
      'Goiás', 'Maranhão', 'Mato Grosso', 'Mato Grosso do Sul', 'Minas Gerais', 'Pará', 'Paraíba',
      'Paraná', 'Pernambuco', 'Piauí', 'Rio de Janeiro', 'Rio Grande do Norte', 'Rio Grande do Sul',
      'Rondônia', 'Roraima', 'Santa Catarina', 'São Paulo', 'Sergipe', 'Tocantins',
    ]),
    localities: {},
  },
  Colombia: {
    regionLabel: 'Departamento',
    localityLabel: 'Municipio',
    regions: r([
      'Amazonas', 'Antioquia', 'Arauca', 'Atlántico', 'Bogotá', 'Bolívar', 'Boyacá', 'Caldas', 'Caquetá',
      'Casanare', 'Cauca', 'Cesar', 'Chocó', 'Córdoba', 'Cundinamarca', 'Guainía', 'Guaviare', 'Huila',
      'La Guajira', 'Magdalena', 'Meta', 'Nariño', 'Norte de Santander', 'Putumayo', 'Quindío', 'Risaralda',
      'San Andrés y Providencia', 'Santander', 'Sucre', 'Tolima', 'Valle del Cauca', 'Vaupés', 'Vichada',
    ]),
    localities: {},
  },
  Ecuador: {
    regionLabel: 'Provincia',
    localityLabel: 'Cantón',
    regions: r([
      'Azuay', 'Bolívar', 'Cañar', 'Carchi', 'Chimborazo', 'Cotopaxi', 'El Oro', 'Esmeraldas', 'Galápagos',
      'Guayas', 'Imbabura', 'Loja', 'Los Ríos', 'Manabí', 'Morona Santiago', 'Napo', 'Orellana', 'Pastaza',
      'Pichincha', 'Santa Elena', 'Santo Domingo de los Tsáchilas', 'Sucumbíos', 'Tungurahua', 'Zamora Chinchipe',
    ]),
    localities: {},
  },
  México: {
    regionLabel: 'Estado',
    localityLabel: 'Municipio',
    regions: r([
      'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua',
      'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco',
      'México', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro',
      'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala',
      'Veracruz', 'Yucatán', 'Zacatecas',
    ]),
    localities: {},
  },
  Paraguay: {
    regionLabel: 'Departamento',
    localityLabel: 'Distrito',
    regions: r([
      'Asunción', 'Alto Paraguay', 'Alto Paraná', 'Amambay', 'Boquerón', 'Caaguazú', 'Caazapá', 'Canindeyú',
      'Central', 'Concepción', 'Cordillera', 'Guairá', 'Itapúa', 'Misiones', 'Ñeembucú', 'Paraguarí',
      'Presidente Hayes', 'San Pedro',
    ]),
    localities: {},
  },
  Uruguay: {
    regionLabel: 'Departamento',
    localityLabel: 'Localidad',
    regions: r([
      'Artigas', 'Canelones', 'Cerro Largo', 'Colonia', 'Durazno', 'Flores', 'Florida', 'Lavalleja',
      'Maldonado', 'Montevideo', 'Paysandú', 'Río Negro', 'Rivera', 'Rocha', 'Salto', 'San José',
      'Soriano', 'Tacuarembó', 'Treinta y Tres',
    ]),
    localities: {},
  },
  'Estados Unidos': {
    regionLabel: 'Estado',
    localityLabel: 'Ciudad',
    regions: r([
      'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
      'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
      'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
      'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
      'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
      'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
      'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia',
    ]),
    localities: {},
  },
  Otro: {
    regionLabel: 'Región',
    localityLabel: 'Comuna',
    regions: [],
    localities: {},
    freeText: true,
  },
};

export const getCountryGeography = (country) => {
  const key = canonicalCountry(country);
  return GEOGRAPHY[key] || GEOGRAPHY.Otro;
};

export const localitiesForRegion = (country, region) => {
  const geo = getCountryGeography(country);
  if (!region) return [];
  return Array.isArray(geo.localities?.[region]) ? geo.localities[region] : [];
};
